/**
 * Info popup — an informative modal shown right after a product is added to the cart.
 *
 * Content lives on the product (`custom.info_popup` metafield -> `info_popup` metaobject)
 * and is rendered by `sections/info-popup.liquid`. This script fetches that section in the
 * context of the product that was just added, and — when there is content — shows the popup
 * instead of the cart drawer. The cart drawer opens once the popup is dismissed.
 */
if (!customElements.get('info-popup-modal')) {
  customElements.define(
    'info-popup-modal',
    class InfoPopupModal extends ModalComponent {
      get isLockingNeeded() {
        return true;
      }

      get requiresBodyAppended() {
        // In the theme editor the section is re-rendered on every change. Keeping the
        // modal inside its section container lets the old instance be removed with it,
        // instead of piling up detached copies on <body>.
        return !(window.Shopify && Shopify.designMode);
      }

      connectedCallback() {
        super.connectedCallback();

        if (window.Shopify && Shopify.designMode) {
          document.querySelectorAll('body > info-popup-modal').forEach((modal) => {
            if (modal !== this) modal.remove();
          });
        }
      }

      get contentElement() {
        return this.querySelector('.info-popup__content');
      }

      setContent(fragment) {
        const content = this.contentElement;
        if (!content) return;

        content.innerHTML = '';
        content.appendChild(fragment);
      }

      clearContent() {
        const content = this.contentElement;
        if (content) content.innerHTML = '';
      }
    }
  );
}

(() => {
  const SECTION_TEMPLATE_ID = 'InfoPopup-content';
  const FETCH_TIMEOUT = 3000;

  const cache = new Map();

  let sectionId = null;
  let suppressCartDrawer = false;
  let pendingCartDrawerArgs = null;
  let isNavigatingAway = false;
  let releaseTimer = null;

  const getModal = () => document.querySelector('info-popup-modal');
  const getCartDrawer = () => document.querySelector('cart-drawer');

  const getSectionId = (modal) => {
    if (sectionId) return sectionId;

    try {
      sectionId = FoxTheme.utils.getSectionId(modal);
    } catch (e) {
      sectionId = null;
    }

    return sectionId;
  };

  const getRootUrl = () => {
    const root =
      (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) ||
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      '/';

    return root.endsWith('/') ? root : `${root}/`;
  };

  const handleFromUrl = (url) => {
    if (!url) return null;
    const match = url.split('?')[0].match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  };

  /**
   * The cart drawer is opened synchronously by ProductForm right after the
   * `product-ajax:added` event. Patching the instance lets us hold that call back
   * until we know whether this product has a popup to show.
   */
  const interceptCartDrawer = () => {
    const cartDrawer = getCartDrawer();
    if (!cartDrawer || cartDrawer.infoPopupPatched) return;

    const originalShow = cartDrawer.show.bind(cartDrawer);
    cartDrawer.infoPopupPatched = true;
    cartDrawer.infoPopupShow = originalShow;
    cartDrawer.show = (activeElement = null, animate = true) => {
      if (suppressCartDrawer) {
        pendingCartDrawerArgs = [activeElement, animate];
        return;
      }

      return originalShow(activeElement, animate);
    };
  };

  const openCartDrawer = () => {
    const cartDrawer = getCartDrawer();
    if (!cartDrawer || !pendingCartDrawerArgs) {
      pendingCartDrawerArgs = null;
      return;
    }

    const args = pendingCartDrawerArgs;
    pendingCartDrawerArgs = null;

    const show = cartDrawer.infoPopupShow || cartDrawer.show.bind(cartDrawer);
    show(...args);
  };

  const release = ({ openCart = true } = {}) => {
    clearTimeout(releaseTimer);
    suppressCartDrawer = false;

    if (openCart && !isNavigatingAway) {
      openCartDrawer();
    } else {
      pendingCartDrawerArgs = null;
    }
  };

  const fetchContent = (handle, id) => {
    if (cache.has(handle)) return cache.get(handle);

    const request = fetch(`${getRootUrl()}products/${handle}?section_id=${id}`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((responseText) => {
        if (!responseText) return null;

        const template = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector(`#${SECTION_TEMPLATE_ID}`);

        return template ? template.innerHTML : null;
      })
      .catch(() => null);

    cache.set(handle, request);

    return request;
  };

  const bindContentActions = (modal) => {
    const dismissButton = modal.querySelector('.info-popup__dismiss');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => modal.hide());
    }

    const productLink = modal.querySelector('.info-popup__link');
    if (productLink) {
      productLink.addEventListener('click', () => {
        // Leaving for the product page — the cart drawer should not open behind it.
        isNavigatingAway = true;
        pendingCartDrawerArgs = null;
      });
    }
  };

  const showPopup = (modal, html) => {
    const fragment = document.createRange().createContextualFragment(html);

    modal.setContent(fragment);
    bindContentActions(modal);

    modal.addEventListener(
      modal.events.handleAfterHide,
      () => {
        release();
        modal.clearContent();
      },
      { once: true }
    );

    // The cart drawer stays suppressed until the popup is dismissed.
    clearTimeout(releaseTimer);

    modal.show();
  };

  // Warm the cache while the add-to-cart request is still in flight.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || !form.action.includes('/cart/add')) return;

      const modal = getModal();
      if (!modal) return;

      const id = getSectionId(modal);
      if (!id) return;

      const context = form.closest('[data-url], .product-card, .card-product, .product') || document;
      const productUrl =
        (context.dataset && context.dataset.url) ||
        (context.querySelector && context.querySelector('a[href*="/products/"]')
          ? context.querySelector('a[href*="/products/"]').getAttribute('href')
          : null);

      const handle = handleFromUrl(productUrl);
      if (handle) fetchContent(handle, id);
    },
    true
  );

  document.addEventListener('product-ajax:added', (event) => {
    const modal = getModal();
    if (!modal) return;

    const id = getSectionId(modal);
    if (!id) return;

    const added = (event.detail && event.detail.product) || {};
    const item = added.items ? added.items[0] : added;
    const handle = (item && item.handle) || handleFromUrl(item && item.url);
    if (!handle) return;

    isNavigatingAway = false;
    suppressCartDrawer = true;
    pendingCartDrawerArgs = null;
    interceptCartDrawer();

    // Never leave the cart drawer suppressed because of a slow or failed request.
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(() => release(), FETCH_TIMEOUT);

    fetchContent(handle, id).then((html) => {
      if (!suppressCartDrawer) return;

      if (html && html.trim()) {
        showPopup(modal, html);
      } else {
        release();
      }
    });
  });
})();
